import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getAuth } from "firebase/auth";
import {
  USER_PROFILE_STORAGE_KEY,
  createLocalStorageJSONStorage,
  clearUserSessionStorage,
} from "./appStorage";
import { ellipsizeProfileText, trimAndLimitProfileText } from "../utils/profileText";
import { buildAuthFallbackProfile } from "../utils/profileSync";

function normalizeUserProfile(userProfile) {
  if (!userProfile) {
    return null;
  }

  const normalizedProfile = { ...userProfile };
  normalizedProfile.displayName = trimAndLimitProfileText(normalizedProfile.displayName);
  normalizedProfile.userName = trimAndLimitProfileText(normalizedProfile.userName);
  normalizedProfile.publicHandle = trimAndLimitProfileText(normalizedProfile.publicHandle);

  if (!normalizedProfile.uid) {
    const currentUser = getAuth().currentUser;
    if (currentUser?.uid) {
      normalizedProfile.uid = currentUser.uid;
    }
  }

  return normalizedProfile;
}

function areProfilesEqual(a, b) {
  if (a === b) {
    return true;
  }

  if (!a || !b || typeof a !== typeof b) {
    return false;
  }

  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) {
      return false;
    }

    for (let i = 0; i < a.length; i += 1) {
      if (!areProfilesEqual(a[i], b[i])) {
        return false;
      }
    }

    return true;
  }

  if (typeof a !== "object") {
    return a === b;
  }

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) {
    return false;
  }

  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) {
      return false;
    }

    if (!areProfilesEqual(a[key], b[key])) {
      return false;
    }
  }

  return true;
}

const useUserProfileStore = create(
  persist(
    (set) => ({
      userProfile: null,
      // Whether this session has seen the account's real Firestore profile yet.
      // Deliberately NOT persisted: a rehydrated profile is last session's truth,
      // good enough to render with but not to decide entitlement denials on.
      // "auth-fallback" writes are identity-only and never resolve it.
      profileSyncStatus: "unknown",
      setUserProfile: (userProfile, { source = "firestore" } = {}) => {
        const isAuthFallback = source === "auth-fallback";
        set((state) => {
          // Identity-only writes merge over what is already known about this
          // account instead of replacing it, so a Firestore outage can never
          // strip entitlements (subscriptionData) from the store.
          const nextUserProfile = normalizeUserProfile(
            isAuthFallback
              ? buildAuthFallbackProfile(userProfile, state.userProfile)
              : userProfile
          );
          if (!nextUserProfile) {
            console.warn("Attempting to set undefined userProfile");
            return state;
          }

          // A fallback write must never demote a state we already resolved.
          const nextSyncStatus = isAuthFallback
            ? state.profileSyncStatus === "synced"
              ? "synced"
              : "degraded"
            : "synced";

          if (areProfilesEqual(state.userProfile, nextUserProfile)) {
            // The profile is unchanged, but the sync status may still advance —
            // the first snapshot after a reload usually matches what was
            // rehydrated, and that is exactly the write that resolves us.
            return state.profileSyncStatus === nextSyncStatus
              ? state
              : { profileSyncStatus: nextSyncStatus };
          }

          return { userProfile: nextUserProfile, profileSyncStatus: nextSyncStatus };
        });
      },
      clearUserProfile: () => {
        set({ userProfile: null, profileSyncStatus: "unknown" });
        clearUserSessionStorage();
      },
    }),
    {
      name: USER_PROFILE_STORAGE_KEY,
      storage: createLocalStorageJSONStorage(),
      partialize: (state) => ({ userProfile: state.userProfile }),
    }
  )
);

export const selectUserProfile = (state) => state.userProfile;
export const selectUserProfileId = (state) => state.userProfile?.uid ?? null;
export const selectUserProfileImage = (state) => state.userProfile?.profileImage ?? "";
export const selectUserDisplayName = (state) =>
  ellipsizeProfileText(state.userProfile?.displayName ?? "");
export const selectUserSubscriptionData = (state) =>
  state.userProfile?.subscriptionData ?? null;
export const selectUserSubscriptionTier = (state) =>
  state.userProfile?.subscriptionData?.tier ?? "";
// True once this session has read the account's real Firestore profile. Gates that
// deny access (member-exclusive capsules) must consult this first: absent
// subscriptionData means "free" only when we actually know, never when we are still
// loading or running on a degraded fallback.
export const selectIsSubscriptionResolved = (state) => state.profileSyncStatus === "synced";
export const selectSetUserProfile = (state) => state.setUserProfile;
export const selectClearUserProfile = (state) => state.clearUserProfile;

export default useUserProfileStore;
